import { useRef, useCallback, useMemo, useState } from "react";
import MapGL, { Source, Layer, Popup, MapRef } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import type { LayerProps } from "react-map-gl/maplibre";
import type { MapLayerMouseEvent } from "maplibre-gl";
import type { FeatureCollection, Feature } from "geojson";
import { Box, Button, ButtonGroup, IconButton, Typography } from "@mui/material";
import { Fullscreen, FullscreenExit } from "@mui/icons-material";
import { Mission, Sample, Core } from "@rogoag/shared-ts";
import bbox from "@turf/bbox";
import { DEFAULT_TILE_SOURCE } from "../JobMap/tileSources";

interface MissionMapProps {
	mission: Mission;
	height?: number | string;
}

interface PointPopup {
	lng: number;
	lat: number;
	type: "sample" | "core";
	data: Sample | Core;
}

const SAMPLE_TYPE_LABELS: Record<number, string> = {
	0: "Regular",
	1: "ISNT 1",
	2: "ISNT 2",
	3: "Cyst",
};

const polygonFillLayer: LayerProps = {
	id: "mission-polygon-fill",
	type: "fill",
	paint: {
		"fill-color": "#2196f3",
		"fill-opacity": 0.3,
	},
};

const polygonLineLayer: LayerProps = {
	id: "mission-polygon-line",
	type: "line",
	paint: {
		"line-color": "#2196f3",
		"line-width": 2,
	},
};

const lineStringLayer: LayerProps = {
	id: "mission-linestring",
	type: "line",
	paint: {
		"line-color": "#ff9800",
		"line-width": 2,
	},
};

const pointCircleLayer: LayerProps = {
	id: "mission-points",
	type: "circle",
	paint: {
		"circle-radius": 5,
		"circle-color": [
			"case",
			// Red for cores missing pulled coordinates (values may be number 0 or string "0")
			[
				"all",
				["has", "core_location"],
				[
					"any",
					["!", ["has", "pulled_lat"]],
					["!", ["has", "pulled_lon"]],
					["==", ["to-number", ["get", "pulled_lat"], 0], 0],
					["==", ["to-number", ["get", "pulled_lon"], 0], 0],
				],
			],
			"#ef5350",
			"#ffffff",
		],
		"circle-stroke-width": 1.5,
		"circle-stroke-color": "#666",
	},
};

const pointLabelLayer: LayerProps = {
	id: "mission-point-labels",
	type: "symbol",
	layout: {
		"text-field": ["get", "rogo_id"],
		"text-size": 11,
		"text-offset": [0, -1.2],
		"text-anchor": "bottom",
		"text-allow-overlap": false,
	},
	paint: {
		"text-color": "#333",
		"text-halo-color": "#fff",
		"text-halo-width": 1.5,
	},
};

type PointView = "samples" | "cores" | "all";

function splitFeatures(fc: FeatureCollection) {
	const polygons: Feature[] = [];
	const samples: Feature[] = [];
	const cores: Feature[] = [];
	const lines: Feature[] = [];

	for (const f of fc.features) {
		const type = f.geometry.type;
		if (type === "Polygon" || type === "MultiPolygon") {
			polygons.push(f);
		} else if (type === "Point" || type === "MultiPoint") {
			if (f.properties?.core_location) {
				cores.push(f);
			} else {
				samples.push(f);
			}
		} else if (type === "LineString" || type === "MultiLineString") {
			lines.push(f);
		}
	}

	return {
		polygonFC: { type: "FeatureCollection" as const, features: polygons },
		sampleFC: { type: "FeatureCollection" as const, features: samples },
		coreFC: { type: "FeatureCollection" as const, features: cores },
		allPointFC: { type: "FeatureCollection" as const, features: [...samples, ...cores] },
		lineFC: { type: "FeatureCollection" as const, features: lines },
	};
}

function formatTimestamp(ts: string | number | undefined): string {
	if (!ts) return "—";
	let ms: number;
	if (typeof ts === "string") {
		ms = new Date(ts).getTime();
	} else if (ts > 1e15) {
		ms = ts / 1000;
	} else if (ts > 1e12) {
		ms = ts;
	} else {
		ms = ts * 1000;
	}
	// Catch bogus dates from upstream (e.g. microseconds parsed as ms → year 58167)
	if (ms > 1e15) ms = ms / 1000;
	const date = new Date(ms);
	if (isNaN(date.getTime()) || date.getTime() === 0) return "—";
	return date.toLocaleString();
}

function PopupRow({ label, value }: { label: string; value: string | number | undefined }) {
	if (!value && value !== 0) return null;
	return (
		<Typography variant="body2" sx={{ fontSize: "0.75rem", lineHeight: 1.4 }}>
			<strong>{label}:</strong> {value}
		</Typography>
	);
}

function SamplePopupContent({ sample }: { sample: Sample }) {
	const ext = sample.extendedData;
	return (
		<Box sx={{ minWidth: 180, maxWidth: 280 }}>
			<Typography variant="subtitle2" sx={{ mb: 0.5, borderBottom: 1, borderColor: "divider", pb: 0.5 }}>
				Sample {sample.id}
			</Typography>
			<PopupRow label="Sample ID" value={ext.sample_id} />
			<PopupRow label="Bag ID" value={ext.bag_id} />
			<PopupRow label="Order" value={ext.order} />
			<PopupRow label="Type" value={SAMPLE_TYPE_LABELS[ext.sample_type as number] ?? ext.sample_type} />
			<PopupRow
				label="Depth"
				value={ext.start_depth && ext.end_depth ? `${ext.start_depth}–${ext.end_depth}"` : undefined}
			/>
			<PopupRow label="Core Length" value={ext.core_length_cm ? `${ext.core_length_cm} cm` : undefined} />
			<PopupRow label="Cores" value={sample.cores?.length} />
			<PopupRow label="Lat" value={sample.coordinates?.[1]?.toFixed(6)} />
			<PopupRow label="Lng" value={sample.coordinates?.[0]?.toFixed(6)} />
			<PopupRow label="Sampled" value={formatTimestamp(ext.sampled_at)} />
			<PopupRow label="Scanned" value={formatTimestamp(ext.scanned_at)} />
			<PopupRow label="Pulled" value={formatTimestamp(ext.pulled_at)} />
			<PopupRow label="Box UID" value={ext.box_uid} />
			{ext.change_type && ext.change_type !== "None" && (
				<PopupRow label="Change" value={`${ext.change_type}${ext.change_reason ? `: ${ext.change_reason}` : ""}`} />
			)}
		</Box>
	);
}

function CorePopupContent({ core }: { core: Core }) {
	const ext = core.extendedData;
	return (
		<Box sx={{ minWidth: 180, maxWidth: 280 }}>
			<Typography variant="subtitle2" sx={{ mb: 0.5, borderBottom: 1, borderColor: "divider", pb: 0.5 }}>
				Core {core.id}
			</Typography>
			<PopupRow label="Sample ID" value={ext.sample_id} />
			<PopupRow label="Bag ID" value={ext.bag_id} />
			<PopupRow label="Order" value={ext.order} />
			<PopupRow label="Type" value={SAMPLE_TYPE_LABELS[ext.sample_type as number] ?? ext.sample_type} />
			<PopupRow
				label="Depth"
				value={ext.start_depth && ext.end_depth ? `${ext.start_depth}–${ext.end_depth}"` : undefined}
			/>
			<PopupRow label="Core Length" value={ext.core_length_cm ? `${ext.core_length_cm} cm` : undefined} />
			<PopupRow label="Planned" value={`${core.plannedLatitude?.toFixed(6)}, ${core.plannedLongitude?.toFixed(6)}`} />
			<PopupRow label="Pulled" value={`${core.pulledLatitude?.toFixed(6)}, ${core.pulledLongitude?.toFixed(6)}`} />
			<PopupRow label="Sampled" value={formatTimestamp(ext.sampled_at)} />
			<PopupRow label="Scanned" value={formatTimestamp(ext.scanned_at)} />
			<PopupRow label="Pulled At" value={formatTimestamp(ext.pulled_at)} />
			<PopupRow label="Box UID" value={ext.box_uid} />
			{ext.change_type && ext.change_type !== "None" && <PopupRow label="Change" value={String(ext.change_type)} />}
		</Box>
	);
}

export function MissionMap({ mission, height = 300 }: MissionMapProps) {
	const mapRef = useRef<MapRef>(null);
	const geojson = useMemo(() => mission.geojson, [mission]);
	const { polygonFC, sampleFC, coreFC, allPointFC, lineFC } = useMemo(() => splitFeatures(geojson), [geojson]);
	const [popup, setPopup] = useState<PointPopup | null>(null);
	const [pointView, setPointView] = useState<PointView>("samples");
	const [fullscreen, setFullscreen] = useState(false);

	const pointFC = pointView === "samples" ? sampleFC : pointView === "cores" ? coreFC : allPointFC;
	const hasSamples = sampleFC.features.length > 0;
	const hasCores = coreFC.features.length > 0;

	const fitBounds = useCallback(() => {
		const map = mapRef.current;
		if (!map || geojson.features.length === 0) return;
		const [minLng, minLat, maxLng, maxLat] = bbox(geojson);
		map.fitBounds([minLng, minLat, maxLng, maxLat], { padding: 40 });
	}, [geojson]);

	const handleClick = useCallback(
		(e: MapLayerMouseEvent) => {
			const feature = e.features?.[0];
			if (!feature) {
				setPopup(null);
				return;
			}

			const rogoId = feature.properties?.rogo_id as string | undefined;
			const lng = e.lngLat.lng;
			const lat = e.lngLat.lat;

			const searchSamples = pointView !== "cores";
			const searchCores = pointView !== "samples";

			// Try to find by rogo_id first
			if (rogoId) {
				if (searchCores) {
					const core = mission.cores.find((c) => c.id === rogoId);
					if (core) {
						setPopup({ lng, lat, type: "core", data: core });
						return;
					}
				}
				if (searchSamples) {
					const sample = mission.centroids.find((s) => s.id === rogoId);
					if (sample) {
						setPopup({ lng, lat, type: "sample", data: sample });
						return;
					}
				}
			}

			// Fallback: find closest point by coordinates
			const candidates: (Sample | Core)[] = [
				...(searchSamples ? mission.centroids : []),
				...(searchCores ? mission.cores : []),
			];
			let closest: (Sample | Core) | null = null;
			let minDist = Infinity;
			for (const pt of candidates) {
				const dx = pt.longitude - lng;
				const dy = pt.latitude - lat;
				const dist = dx * dx + dy * dy;
				if (dist < minDist) {
					minDist = dist;
					closest = pt;
				}
			}
			if (closest) {
				setPopup({
					lng,
					lat,
					type: closest instanceof Sample ? "sample" : "core",
					data: closest,
				});
			}
		},
		[mission, pointView],
	);

	const handleMouseEnter = useCallback(() => {
		const map = mapRef.current;
		if (map) map.getCanvas().style.cursor = "pointer";
	}, []);

	const handleMouseLeave = useCallback(() => {
		const map = mapRef.current;
		if (map) map.getCanvas().style.cursor = "";
	}, []);

	if (geojson.features.length === 0) {
		return (
			<Box
				sx={{
					height,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
				}}
			>
				<Typography variant="body2" color="text.secondary">
					No mission data
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				height: fullscreen ? "100vh" : height,
				borderRadius: fullscreen ? 0 : 1,
				overflow: "hidden",
				position: fullscreen ? "fixed" : "relative",
				...(fullscreen && { top: 0, left: 0, right: 0, bottom: 0, zIndex: 1300 }),
			}}
		>
			{hasSamples && hasCores && (
				<ButtonGroup
					size="small"
					sx={{
						position: "absolute",
						top: 8,
						left: 8,
						zIndex: 1,
						bgcolor: "background.paper",
						boxShadow: 1,
					}}
				>
					<Button variant={pointView === "samples" ? "contained" : "outlined"} onClick={() => setPointView("samples")}>
						Samples
					</Button>
					<Button variant={pointView === "cores" ? "contained" : "outlined"} onClick={() => setPointView("cores")}>
						Cores
					</Button>
					<Button variant={pointView === "all" ? "contained" : "outlined"} onClick={() => setPointView("all")}>
						All
					</Button>
				</ButtonGroup>
			)}
			<IconButton
				size="small"
				onClick={() => {
					setFullscreen((prev) => !prev);
					// Re-fit bounds after resize
					setTimeout(() => {
						mapRef.current?.resize();
						fitBounds();
					}, 50);
				}}
				sx={{
					position: "absolute",
					top: 8,
					right: 8,
					zIndex: 1,
					bgcolor: "background.paper",
					boxShadow: 1,
					"&:hover": { bgcolor: "action.hover" },
				}}
			>
				{fullscreen ? <FullscreenExit /> : <Fullscreen />}
			</IconButton>
			<MapGL
				ref={mapRef}
				initialViewState={{ latitude: 40, longitude: -95, zoom: 4 }}
				style={{ width: "100%", height: "100%" }}
				mapStyle={DEFAULT_TILE_SOURCE.style}
				onLoad={fitBounds}
				onClick={handleClick}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				interactiveLayerIds={["mission-points"]}
			>
				{polygonFC.features.length > 0 && (
					<Source id="mission-polygons" type="geojson" data={polygonFC}>
						<Layer {...polygonFillLayer} />
						<Layer {...polygonLineLayer} />
					</Source>
				)}
				{lineFC.features.length > 0 && (
					<Source id="mission-lines" type="geojson" data={lineFC}>
						<Layer {...lineStringLayer} />
					</Source>
				)}
				{pointFC.features.length > 0 && (
					<Source id="mission-points" type="geojson" data={pointFC}>
						<Layer {...pointCircleLayer} />
						<Layer {...pointLabelLayer} />
					</Source>
				)}
				{popup && (
					<Popup
						longitude={popup.lng}
						latitude={popup.lat}
						onClose={() => setPopup(null)}
						closeOnClick={false}
						anchor="bottom"
						maxWidth="300px"
					>
						{popup.type === "sample" ? (
							<SamplePopupContent sample={popup.data as Sample} />
						) : (
							<CorePopupContent core={popup.data as Core} />
						)}
					</Popup>
				)}
			</MapGL>
		</Box>
	);
}
